import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getTeamBillingDetails } from '@/pages/api/scribeAPIV2';
import { type ErrorResponse } from '@/types';
import { isOnPrem } from '@/utils/buildProfile';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { toast } from '@heidi/design-system/toast';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// paths
// Definition moved to `@heidi/commerce/contracts` (C-series, ARCFE-971).
// import + re-export: this module uses the name locally, and a bare
// `export { X } from '...'` does not bind it here.
import { GET_TEAM_BILLING_DETAILS } from '@heidi/commerce/contracts';

export { GET_TEAM_BILLING_DETAILS };

export const invalidateCurrentTeamBillingInfo = (teamId?: string) =>
  reactQueryClient.invalidateQueries({
    queryKey: [GET_TEAM_BILLING_DETAILS, { teamId }],
  });

export function useQueryTeamBilling(teamId?: string, isTeamAdmin?: boolean) {
  const { isAuthenticated } = useIsAuthenticated();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  return useQuery({
    queryKey: [GET_TEAM_BILLING_DETAILS, { teamId }],
    enabled:
      !isOnPrem &&
      isAuthenticated &&
      !!teamId &&
      isTeamAdmin &&
      teamDetails?.team.is_stripe_managed,
    queryFn: async () => {
      try {
        if (teamId) {
          const { data } = await getTeamBillingDetails(teamId);
          return data;
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          const errorData = error.response?.data as ErrorResponse;
          toast({
            tone: 'destructive',
            title: 'Failed to get billing details',
            description: errorData.detail.msg,
          });
        }
      }
    },
  });
}
